import { Link } from "react-router-dom";
import { ChevronRight, ArrowRight, Sparkles, Tag, Percent } from "lucide-react";

interface Category {
  id: string;
  name: string;
  slug: string;
  image?: string;
  productCount?: number;
}

interface FeaturedProduct {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  image: string;
  badge?: string;
}

interface MegaMenuSimpleProps {
  isOpen: boolean;
  onClose: () => void;
  categories: Category[];
  featuredProducts?: FeaturedProduct[];
  showPromoBanners?: boolean;
}

// Demo categories for smaller shops
const defaultCategories: Category[] = [
  { id: "1", name: "Tričká", slug: "tricka", image: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=200&h=200&fit=crop", productCount: 42 },
  { id: "2", name: "Mikiny", slug: "mikiny", image: "https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=200&h=200&fit=crop", productCount: 28 },
  { id: "3", name: "Čiapky", slug: "ciapky", image: "https://images.unsplash.com/photo-1576871337632-b9aef4c17ab9?w=200&h=200&fit=crop", productCount: 15 },
  { id: "4", name: "Tašky", slug: "tasky", image: "https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=200&h=200&fit=crop", productCount: 12 },
  { id: "5", name: "Plagáty", slug: "plagaty", image: "https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=200&h=200&fit=crop", productCount: 36 },
  { id: "6", name: "Príslušenstvo", slug: "prislusenstvo", image: "https://images.unsplash.com/photo-1523170335258-f5ed11844a49?w=200&h=200&fit=crop", productCount: 8 },
];

const quickLinks = [
  { icon: Sparkles, label: "Novinky", slug: "novinky", color: "text-amber-600 bg-amber-500/10 hover:bg-amber-500/20" },
  { icon: Tag, label: "Výpredaj", slug: "vypredaj", color: "text-destructive bg-destructive/10 hover:bg-destructive/20" },
  { icon: Percent, label: "Balíky", slug: "baliky", color: "text-purple-600 bg-purple-500/10 hover:bg-purple-500/20" },
];

const demoFeaturedProduct: FeaturedProduct = {
  id: "featured-1",
  name: "Limitovaná edícia tričko",
  price: 24.99,
  originalPrice: 34.99,
  image: "https://images.unsplash.com/photo-1618354691373-d851c5c3a990?w=200&h=200&fit=crop",
  badge: "HOT",
};

export const MegaMenuSimple = ({
  isOpen,
  onClose,
  categories = defaultCategories,
  featuredProducts = [demoFeaturedProduct],
  showPromoBanners = true,
}: MegaMenuSimpleProps) => {
  if (!isOpen) return null;

  const displayCategories = categories.length > 0 ? categories : defaultCategories;
  const product = featuredProducts[0] || demoFeaturedProduct;

  return (
    <div 
      className="absolute left-0 right-0 top-full z-[110] bg-background border-b border-border shadow-2xl animate-slide-down" 
      style={{ animationDuration: '200ms' }}
    >
      <div className="max-w-[1200px] mx-auto px-4 lg:px-6 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Left - Categories as visual grid */}
          <div className="col-span-8">
            <h4 className="text-xs font-bold text-primary uppercase tracking-widest mb-4">
              Kategórie
            </h4>
            <div className="grid grid-cols-3 gap-3">
              {displayCategories.map((category, index) => (
                <Link
                  key={category.id}
                  to={`/kategoria/${category.slug}`}
                  onClick={onClose}
                  className="group flex items-center gap-3 p-3 rounded-xl bg-secondary/50 hover:bg-secondary transition-all animate-slide-up"
                  style={{ animationDelay: `${index * 30}ms`, animationFillMode: 'backwards' }}
                >
                  {category.image && (
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-secondary">
                      <img
                        src={category.image}
                        alt={category.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                      {category.name}
                    </p>
                    {category.productCount && (
                      <p className="text-xs text-muted-foreground">
                        {category.productCount} produktov
                      </p>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
                </Link>
              ))}
            </div>

            {/* Quick links */}
            <div className="flex gap-2 mt-4 pt-4 border-t border-border">
              {quickLinks.map((link) => (
                <Link
                  key={link.slug}
                  to={`/${link.slug}`}
                  onClick={onClose}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${link.color}`}
                >
                  <link.icon className="w-4 h-4" />
                  {link.label}
                </Link>
              ))}
              <Link
                to="/vsetko"
                onClick={onClose}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground bg-secondary hover:bg-secondary/80 transition-colors ml-auto"
              >
                Zobraziť všetko
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Right - Featured Product */}
          <div className="col-span-4">
            <h4 className="text-xs font-bold text-primary uppercase tracking-widest mb-4">
              Odporúčame
            </h4>
            <Link
              to={`/produkt/${product.id}`}
              onClick={onClose}
              className="block group relative overflow-hidden rounded-2xl bg-gradient-to-br from-secondary via-secondary/80 to-primary/10 hover:to-primary/20 transition-all"
            >
              <div className="flex flex-col p-4">
                <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-secondary mb-4">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {product.badge && (
                    <div className="absolute top-2 left-2 px-2.5 py-1 rounded-lg bg-primary text-primary-foreground text-xs font-bold shadow-lg">
                      {product.badge}
                    </div>
                  )}
                </div>
                <p className="text-base font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-2">
                  {product.name}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xl font-black text-primary">
                    {product.price.toFixed(2).replace('.', ',')} €
                  </span>
                  {product.originalPrice && (
                    <span className="text-sm text-muted-foreground line-through">
                      {product.originalPrice.toFixed(2).replace('.', ',')} €
                    </span>
                  )}
                </div>
              </div>
              <div className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-primary flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all shadow-lg">
                <ArrowRight className="w-5 h-5 text-primary-foreground" />
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
